<template>
  <v-card>
    <v-card-title class="mb-0">
      <div class="d-flex flex-column">
        <span class="headline"
          ><slot :name="!items.length && normalizedErrors.length ? 'error-title' : 'title'"
        /></span>
      </div>
      <v-spacer />
    </v-card-title>

    <div class="overflow-y-auto">
      <v-card-text v-if="items.length">
        <div class="summary pl-2">
          <slot name="summary" />
        </div>

        <AgTableClient
          class="mt-4"
          :column-defs="dataColDefs"
          :get-row-id="(item) => item.row"
          no-menu
          no-move
          no-sort
          :row-data="items"
          style="height: 30vh"
          table-id="UploadOrdersPreviewTable"
        />

        <v-row>
          <v-col>
            <v-divider />
          </v-col>
        </v-row>

        <v-row v-if="showError || showSuccess">
          <v-col class="pa-0 px-1 col-6 offset-3">
            <div
              class="v-alert v-alert--dense text--primary text-body-2 text-center"
              :class="{ error: showError, success: showSuccess }"
            >
              <div v-if="showError">
                {{ errorMsg }}
              </div>
              <div v-if="showSuccess">
                <slot name="success-message" />
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text v-if="normalizedErrors.length > 0">
        <div class="summary pl-2">
          {{ normalizedErrors.length }} orders creation failed or was updated
        </div>

        <AgTableClient
          class="mt-4 warning--text"
          :column-defs="errorColDefs"
          :get-row-id="(item) => item.row"
          no-menu
          no-move
          no-sort
          :row-data="normalizedErrors"
          style="height: 192px"
          table-id="UploadOrdersErrorTable"
        />
      </v-card-text>
    </div>

    <v-card-actions class="d-flex">
      <div class="d-flex flex-grow-1 justify-space-between align-end">
        <v-btn class="mt-6" color="secondary" @click="$emit('back')"> Back </v-btn>
        <aurora-btn
          v-if="items.length && !showError"
          class="mt-6"
          color="primary"
          :disabled="isCreating"
          :timeframe="timeframe"
          @click="submit()"
        >
          <slot name="create-button-text" />
        </aurora-btn>
      </div>
    </v-card-actions>

    <v-progress-linear v-if="isCreating" color="primary" indeterminate />
  </v-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import Component from 'vue-class-component';
import wait from '@/modules/common/services/wait';
import { ApiError } from '@/utils/errors';
import type { UploadMethod } from '@/modules/common/types/upload';
import {
  SponsoredLoanBasketResponse,
  SponsoredLoanDetailsResponseItem,
} from '@/modules/sponsorship/models';
import { OmsBasketItem, ParseOmsBasketResponse } from '@/modules/marketplace/models';
import { ErrorItem } from '@/modules/common/models';
import { ColDef } from 'ag-grid-enterprise';
import { AgTableClient } from '@/modules/common/components/ag-table';

@Component({
  props: {
    basketResponse: {
      type: Object as PropType<SponsoredLoanBasketResponse | ParseOmsBasketResponse>,
      required: true,
    },
    timeframe: {
      type: String as PropType<string>,
      required: true,
    },
    uploadMethod: {
      type: String as PropType<UploadMethod>,
      default: '',
    },
    dataColDefs: {
      type: Array as PropType<ColDef[]>,
      required: true,
    },
    errorColDefs: {
      type: Array as PropType<ColDef[]>,
      required: true,
    },
    execute: Function,
  },
  components: {
    AgTableClient,
  },
})
export default class UploadConfirmBasket extends Vue {
  // props
  protected readonly basketResponse!: SponsoredLoanBasketResponse | ParseOmsBasketResponse;
  protected readonly timeframe!: string;
  protected readonly uploadMethod!: UploadMethod;
  protected readonly dataColDefs!: Array<ColDef<SponsoredLoanDetailsResponseItem | OmsBasketItem>>;
  protected readonly errorColDefs!: ColDef[];

  /*
   * Using a callback instead of v-on is an unorthodox solution.
   * Here it permits the whole submit/error worlkflow to be contained inside the component.
   */
  protected readonly execute!: (
    basketResponse: SponsoredLoanBasketResponse | ParseOmsBasketResponse
  ) => Promise<void>;

  protected showSuccess = false;
  protected errorMsg: string | null = null;
  protected isCreating = false;

  protected get items(): unknown[] {
    return 'items' in this.basketResponse ? this.basketResponse.items : this.basketResponse.entries;
  }

  protected get normalizedErrors(): ErrorItem[] {
    return (
      this.basketResponse.errors?.map(
        (error) => ((error.row = this.uploadMethod === 'paste' ? error.row - 1 : error.row), error)
      ) ?? []
    );
  }

  protected get showError(): boolean {
    return !!this.errorMsg;
  }

  protected async submit(): Promise<void> {
    if (this.isCreating) {
      return;
    }

    this.isCreating = true;

    try {
      const waitTimer = wait(800);

      await this.execute(this.basketResponse);

      this.showSuccess = true;

      // wait a minimum amount of time before closing the dialog so that the UI nicely shows the success message
      await waitTimer;
      this.$emit('close-modal');
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.responseData?.msgkey) {
        this.errorMsg = this.$i18n.t(apiError.responseData.msgkey as string) as string;
      } else {
        this.errorMsg = apiError.message;
      }
    } finally {
      this.isCreating = false;
    }
  }
}
</script>
<style lang="scss" scoped>
// max-height: 85vh; is what we really want, but for very small screens we need to go lower
// to make sure the errors are always visible!
@media (min-height: 700px) {
  .v-card {
    max-height: 85vh;
  }
}

.table-container > div {
  display: flex;
  overflow: hidden;
}

.summary:after {
  /* add colon */
  content: ':';
  margin-left: -2px;
}

.summary strong {
  color: white;
}
</style>
